import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { GlobalActions, GlobalGetters } from "@/store/modules/global/types";
import { UserActions, UserGetters } from "@/store/modules/user/types";
import { User, UserProfileSchema } from "@/models/user/user";
import { Component, Vue } from "vue-property-decorator";
import { Roles } from "@/models/user/roles";
import AuthService from "@/services/auth_service";
import UserService from "@/services/user_service";
import { AuthError, GenericError } from "@/services/error_service";
import NotificationService from "@/services/notification_service";
import { Notification } from "@/models/notification";

@Component
export class Auth extends Vue {
  /* GETTERS */
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetLoggedIn))
  isLoggedIn!: boolean;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetRole))
  userRole!: Roles;
  @(namespace(StoreModule.User).Getter(UserGetters.GetUser))
  user!: User;
  @(namespace(StoreModule.User).Getter(UserGetters.GetMsrpMultiplier))
  msrpMultiplier!: number;
  @(namespace(StoreModule.User).Getter(UserGetters.GetFileId))
  fileBeingEditedId!: string;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetNotifications))
  notifications!: Notification[];
  /* ACTIONS */
  @(namespace(StoreModule.Global).Action(GlobalActions.AddLoggedIn))
  setLoggedIn!: (value: boolean) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddRole))
  setUserRole!: (value: Roles | null) => Promise<void>;
  @(namespace(StoreModule.User).Action(UserActions.AddUser))
  setUserInfo!: (value: User | null) => Promise<void>;
  @(namespace(StoreModule.User).Action(UserActions.UpdateUserProfile))
  setUserProfileInfo!: (value: UserProfileSchema) => Promise<void>;
  @(namespace(StoreModule.User).Action(UserActions.AddFileId))
  addFileBeingEditedId!: (value: string) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddNotifications))
  addNotifications!: (value: Notification[]) => Promise<void>;
  protected authService = new AuthService();
  protected userService = new UserService();
  protected notificationService = new NotificationService();

  public async checkAuth(): Promise<void> {
    try {
      await this.authService.checkAuth();
      await this.setLoggedIn(true);
      const user = await this.userService.getUserProfile();
      await this.setUserRole(user.role);
      await this.setUserInfo(user);
      const notifications = await this.notificationService.fetchNotifications();
      await this.addNotifications(notifications);
    } catch (err) {
      await this.addFileBeingEditedId("");
      await this.setLoggedIn(false);
      await this.setUserRole(null);
      await this.setUserInfo(null);
      if (this.notifications.length > 0) {
        await this.addNotifications([]);
      }
    }
  }

  public async logout(): Promise<void> {
    try {
      await this.addFileBeingEditedId("");
      await this.setLoggedIn(false);
      await this.setUserRole(null);
      await this.setUserInfo(null);
      await this.addNotifications([]);
      await this.authService.logout();
      this.$router.push("/");
    } catch (err) {
      if (err instanceof AuthError) {
        AuthError.logout();
      } else {
        GenericError.popup(err.message);
      }
    }
  }
}
